/**
 * Service Song List Component
 * 
 * Manages the list of songs associated with a church service.
 * Features include:
 * - Displaying the current list of songs for a service
 * - Adding new songs through a song selector modal
 * - Managing song keys and arrangements
 * - Handling song reordering within the service
 * - Removing songs from the service
 */

import React, { useState } from 'react';
import {
  List,
  ListItem,
  IconButton,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Link,
  Paper,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  TextField
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import MusicNoteIcon from '@mui/icons-material/MusicNote';
import YouTubeIcon from '@mui/icons-material/YouTube';
import SpotifyIcon from '@mui/icons-material/Audiotrack';
import AddIcon from '@mui/icons-material/Add';
import PlaylistAddIcon from '@mui/icons-material/PlaylistAdd';
import TextFieldsIcon from '@mui/icons-material/TextFields';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { alpha } from '@mui/material/styles';
import { transposeChordChart } from '../../utils/musicUtils';
import { formatChordChartLine as formatChordChartLineUtil } from '../../utils/chordChartUtils';
import SongSelector from './SongSelector'; // Assuming SongSelector is in the same directory
import { useTheme } from '@mui/material/styles';

const NOTES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

const ServiceSongList = React.memo(({ songs = [], onSongsChange, customFetch, onVideoClick }) => {
  const [selectedSong, setSelectedSong] = useState(null);
  const [chordChartOpen, setChordChartOpen] = useState(false);
  const [songSelectorOpen, setSongSelectorOpen] = useState(false);
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState('');
  const theme = useTheme();
  const [currentKey, setCurrentKey] = useState('');
  const [hideChords, setHideChords] = useState(false);
  const [showLyricsOnly, setShowLyricsOnly] = useState(false);

  const handleDragEnd = (result) => {
    if (!result.destination) return;

    const items = Array.from(songs);
    const [reorderedItem] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, reorderedItem);

    // Pass the dragged item's DOM element
    const draggedElement = document.querySelector(`[data-rbd-draggable-id="${result.draggableId}"]`);
    onSongsChange(items, draggedElement);
  };

  const handleDeleteSong = (index) => {
    const newSongs = songs.filter((_, i) => i !== index);
    onSongsChange(newSongs);
  };

  const handleViewChordChart = (song) => {
    console.log('Opening chord chart for song:', song);
    setSelectedSong(song);
    setCurrentKey(song.key);
    setChordChartOpen(true);
  };

  const handleCloseChordChart = () => {
    setChordChartOpen(false);
    setSelectedSong(null);
    setCurrentKey('');
    setShowLyricsOnly(false);
  };

  const handleAddSongs = (selectedSong) => {
    const newSong = {
      song: selectedSong.song,
      key: selectedSong.key,
      defaultKey: selectedSong.defaultKey
    };
    onSongsChange([...songs, newSong]);
    setSongSelectorOpen(false);
  };

  const handleAddItem = () => {
    if (!newItemTitle.trim()) return;
    const newItem = {
      _id: `item-${Date.now()}`,
      title: newItemTitle.trim(),
      type: 'item'
    };
    onSongsChange([...songs, newItem]);
    setNewItemTitle('');
    setAddItemOpen(false);
  };

  const getTransposedChordChart = (song) => {
    console.log('Getting transposed chord chart for song:', song);
    if (!song?.song?.chordChart?.content) {
      console.log('No chord chart content found');
      return '';
    }
    
    const fromKey = song.song.defaultKey || song.key;
    const semitones = calculateSemitones(fromKey, currentKey);
    console.log('Transposing from key:', fromKey, 'to key:', currentKey, 'semitones:', semitones);
    
    const transposed = transposeChordChart(song.song.chordChart.content, semitones);
    return transposed;
  };

  const calculateSemitones = (fromKey, toKey) => {
    const notes = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];
    const fromIndex = notes.indexOf(fromKey);
    const toIndex = notes.indexOf(toKey);
    
    if (fromIndex === -1 || toIndex === -1) return 0;
    
    let semitones = toIndex - fromIndex;
    if (semitones < -6) semitones += 12;
    if (semitones > 6) semitones -= 12;
    
    return semitones;
  };

  const renderStreamingLinks = (song) => {
    // Check if song is nested inside song property (from service data structure)
    const songData = song.song || song;
    if (!songData.streamingLinks || songData.streamingLinks.length === 0) return null;

    return songData.streamingLinks.map((link, index) => {
      if (link.type === 'youtube') {
        return (
          <IconButton
            key={index}
            size="small"
            onClick={() => onVideoClick(link.url, `${songData.title} - YouTube`)}
            sx={{ 
              color: '#FF0000',
              '&:hover': {
                bgcolor: alpha('#FF0000', 0.04)
              }
            }}
          >
            <YouTubeIcon fontSize="small" />
          </IconButton>
        );
      }
      return null;
    });
  };

  const formatChordChartLine = (line) => {
    return formatChordChartLineUtil(line, { showLyricsOnly });
  };

  const handleSaveKey = () => {
    console.log('Saving new key:', currentKey, 'for song:', selectedSong);
    const updatedSongs = songs.map(song => 
      song.song._id === selectedSong.song._id
        ? { ...song, key: currentKey }
        : song
    );
    console.log('Updated songs:', updatedSongs);
    onSongsChange(updatedSongs);
    handleCloseChordChart();
  };

  const SongItem = React.memo(({ song, index, handleDeleteSong, handleViewChordChart, renderStreamingLinks, provided, snapshot, onVideoClick }) => {
    const isCustomItem = song?.type === 'item';
    return (
      <Box
        ref={provided.innerRef}
        {...provided.draggableProps}
        sx={{
          py: 1,
          '&:not(:last-child)': {
            borderBottom: 1,
            borderColor: 'divider'
          }
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Box 
            {...provided.dragHandleProps}
            sx={{ 
              mr: 2,
              cursor: 'grab',
              color: 'text.secondary',
              '&:active': { cursor: 'grabbing' }
            }}
          >
            <DragIndicatorIcon />
          </Box>
          
          <Box sx={{ flexGrow: 1 }}>
            {isCustomItem ? (
              <Typography variant="body1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <TextFieldsIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                {song?.title || 'Untitled Item'}
              </Typography>
            ) : (
              <Typography variant="body1">
                <Box component="span" sx={{ 
                  fontWeight: 'bold', 
                  color: 'purple',
                  mr: 1,
                  fontSize: '1.2rem'
                }}>
                  {song?.key}
                </Box>
                - {song?.song?.title || 'Untitled Song'}
              </Typography>
            )}
          </Box>

          <Box sx={{ display: 'flex', gap: 1 }}>
            {!isCustomItem && song?.song?.youtubeUrl && (
              <IconButton
                size="small"
                onClick={() => onVideoClick(song.song.youtubeUrl, song.song.title)}
                sx={{ 
                  color: '#FF0000',
                  '&:hover': {
                    bgcolor: alpha('#FF0000', 0.04)
                  }
                }}
              >
                <YouTubeIcon fontSize="small" />
              </IconButton>
            )}
            {!isCustomItem && song?.song?.chordChart && (
              <IconButton
                size="small"
                onClick={() => handleViewChordChart(song)}
                sx={{ 
                  color: 'primary.main',
                  '&:hover': {
                    bgcolor: alpha('#1976d2', 0.04)
                  }
                }}
              >
                <MusicNoteIcon fontSize="small" />
              </IconButton>
            )}
            <IconButton
              size="small"
              onClick={() => handleDeleteSong(index)}
              sx={{ 
                color: '#ff5252',
                '&:hover': {
                  bgcolor: alpha('#ff5252', 0.04)
                }
              }}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
      </Box>
    );
  });

  return (
    <>
      <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
        <Button
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={() => setSongSelectorOpen(true)}
          size="small"
        >
          Add Songs
        </Button>
        <Button
          variant="outlined"
          startIcon={<PlaylistAddIcon />}
          onClick={() => setAddItemOpen(true)}
          size="small"
          color="secondary"
        >
          Add Item
        </Button>
      </Box>

      <DragDropContext onDragEnd={handleDragEnd}>
        <Box sx={{ width: '100%', minHeight: '50px' }}>
          <Droppable droppableId="songList">
            {(provided, snapshot) => (
              <Paper
                ref={provided.innerRef}
                {...provided.droppableProps}
                elevation={0}
                sx={{ 
                  p: 2,
                  bgcolor: alpha('#1976d2', 0.04),
                  borderRadius: 2
                }}
              >
                {songs.length > 0 ? (
                  <>
                    {songs.map((song, index) => {
                      const songId = song.type === 'item' ? (song._id || `item-${index}`) : (song.song?._id?.toString() || `temp-${index}`);
                      const draggableId = `song-${songId}`;
                      
                      return (
                        <Draggable 
                          key={draggableId}
                          draggableId={draggableId}
                          index={index}
                        >
                          {(provided, snapshot) => (
                            <SongItem
                              song={song}
                              index={index}
                              handleDeleteSong={handleDeleteSong}
                              handleViewChordChart={handleViewChordChart}
                              renderStreamingLinks={renderStreamingLinks}
                              provided={provided}
                              snapshot={snapshot}
                              onVideoClick={onVideoClick}
                            />
                          )}
                        </Draggable>
                      );
                    })}
                    {provided.placeholder}
                  </>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    No songs added
                  </Typography>
                )}
              </Paper>
            )}
          </Droppable>
        </Box>
      </DragDropContext>

      <SongSelector
        open={songSelectorOpen}
        onClose={() => setSongSelectorOpen(false)}
        onSelect={handleAddSongs}
        customFetch={customFetch}
      />

      {/* Add Custom Item Dialog */}
      <Dialog open={addItemOpen} onClose={() => { setAddItemOpen(false); setNewItemTitle(''); }} maxWidth="xs" fullWidth>
        <DialogTitle>Add Custom Item</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            label="Item Title"
            placeholder="e.g. Worship, Prayer, Offering, Announcements"
            fullWidth
            value={newItemTitle}
            onChange={(e) => setNewItemTitle(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') handleAddItem(); }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setAddItemOpen(false); setNewItemTitle(''); }}>Cancel</Button>
          <Button onClick={handleAddItem} variant="contained" disabled={!newItemTitle.trim()}>Add</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={chordChartOpen}
        onClose={handleCloseChordChart}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <FormControlLabel
              control={
                <Switch
                  checked={showLyricsOnly}
                  onChange={(e) => setShowLyricsOnly(e.target.checked)}
                  name="showLyricsOnly"
                />
              }
              label="View Lyrics Only"
            />
            <Typography variant="h6" component="div">
              {selectedSong?.song?.title} - Chord Chart
            </Typography>
          </Box>
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel id="key-select-label">Key</InputLabel>
            <Select
              labelId="key-select-label"
              value={currentKey}
              label="Key"
              onChange={(e) => setCurrentKey(e.target.value)}
              disabled={showLyricsOnly}
            >
              {NOTES.map((note) => (
                <MenuItem key={note} value={note}>
                  {note}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogTitle>
        <DialogContent>
          {getTransposedChordChart(selectedSong)?.split('\n').map((line, index) => (
            <React.Fragment key={index}>
              {formatChordChartLine(line)}
            </React.Fragment>
          ))}
        </DialogContent>
        <DialogActions>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
            <Button 
              onClick={handleSaveKey}
              color="primary"
              variant="contained"
              disabled={currentKey === selectedSong?.key}
            >
              Save Key
            </Button>
            <Button onClick={handleCloseChordChart}>Close</Button>
          </Box>
        </DialogActions>
      </Dialog>
    </>
  );
});

export default ServiceSongList;
